import { useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

// Subscriptions
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { onUpdateCommissionCycleStatus } from "@/Common/api/commissionCycles/subscriptions";

// Types
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { IUpdateCommissionCycleStatus } from "@/Washouts/types/PayrollClosing/mutations";
import { PAYROLL_CLOSING_KEYS } from "@/Washouts/api/PayrollClosing/constants";
import { onUpdateCommissionCycleToFinalizingProcess } from "@/Washouts/api/PayrollClosing/subscriptions";
import { useSelectionPayrollClosing } from "@/Washouts/context/selectionPayrollClosingContext";

interface IUseCommissionCycleStatusSubscriptionsArgs {
	locationId: string;
}

export const useCommissionCycleStatusSubscriptions = (props: Readonly<IUseCommissionCycleStatusSubscriptionsArgs>) => {
	const { locationId } = props;

	const { addSubscription, getSubscription } = useSubscriptions();
	const { setSelectedCommissionCycle } = useSelectionPayrollClosing();
	const queryClient = useQueryClient();

	const subscription = getSubscription([PAYROLL_CLOSING_KEYS.MUTATION.UPDATE_COMMISSION_CYCLE_STATUS, locationId]);
	subscription?.subscriptions?.onUpdateCommissionCycleStatus?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateCommissionCycleStatus", IUpdateCommissionCycleStatus>) => {
			const response = result.data?.onUpdateCommissionCycleStatus;
			if (response?.updatedCommissionCycle) {
				queryClient.setQueryData(["commissionCycles", locationId], (oldData: ICommissionCycle[]) => {
					return oldData.map((commissionCycle) => {
						if (commissionCycle._id === response.updatedCommissionCycle._id) {
							setSelectedCommissionCycle(response.updatedCommissionCycle);
							return response.updatedCommissionCycle;
						}
						return commissionCycle;
					});
				});
			}
		}
	);

	useEffect(() => {
		if (locationId) {
			addSubscription(
				[PAYROLL_CLOSING_KEYS.MUTATION.UPDATE_COMMISSION_CYCLE_STATUS, locationId],
				[
					{
						context: PAYROLL_CLOSING_KEYS.SUBSCRIPTION.ON_UPDATE_COMMISSION_CYCLE_STATUS,
						query: onUpdateCommissionCycleStatus,
						variables: {
							locationId,
						},
					},
				],
				true
			);
		}
	}, [locationId]);
};

export const useCommissionCycleFinalizingProcessSubscription = (commissionCycle: ICommissionCycle, locationId: string) => {
	const { addSubscription, getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	const { setSelectedCommissionCycle } = useSelectionPayrollClosing();

	const identifierCCToFinalizeProcess = useMemo(
		() => [PAYROLL_CLOSING_KEYS.MUTATION.UPDATE_COMMISSION_CYCLE_TO_FINALIZING_PROCESS, locationId, commissionCycle?._id],
		[locationId, commissionCycle?._id]
	);

	const subscriptions = getSubscription(identifierCCToFinalizeProcess);
	const subscriptionOnUpdateToFinalizingProcess =
		subscriptions?.subscriptions[`${PAYROLL_CLOSING_KEYS.SUBSCRIPTION.ON_UPDATE_COMMISSION_CYCLE_TO_FINALIZING_PROCESS}`];

	const callback = subscriptionOnUpdateToFinalizingProcess?.getCallback();
	if (!callback) {
		subscriptionOnUpdateToFinalizingProcess?.setCallback(
			(response: GraphqlSubscriptionResponse<"onUpdateCommissionCycleToFinalizingProcess", ICommissionCycle>) => {
				if (response.data?.onUpdateCommissionCycleToFinalizingProcess) {
					const commissionCycleUpdated = response.data.onUpdateCommissionCycleToFinalizingProcess;
					queryClient.setQueryData(["commissionCycles", locationId], (oldCCData: ICommissionCycle[]) => {
						return oldCCData.map((cc) => {
							if (cc._id === commissionCycleUpdated._id) {
								setSelectedCommissionCycle(commissionCycleUpdated);
								return commissionCycleUpdated;
							}
							return cc;
						});
					});
				}
			}
		);
	}

	useEffect(() => {
		if (commissionCycle?._id && locationId && identifierCCToFinalizeProcess) {
			addSubscription(
				identifierCCToFinalizeProcess,
				[
					{
						context: PAYROLL_CLOSING_KEYS.SUBSCRIPTION.ON_UPDATE_COMMISSION_CYCLE_TO_FINALIZING_PROCESS,
						query: onUpdateCommissionCycleToFinalizingProcess,
						variables: {
							locationId,
							_id: commissionCycle?._id,
						},
					},
				],
				true
			);
		}
	}, [identifierCCToFinalizeProcess, commissionCycle?._id, locationId, addSubscription]);
};
